/**
 * Permission constants and utilities for proofing flow authorization.
 *
 * This module defines all available permissions in the proofing flow application
 * and provides utilities to fetch and transform permissions from the authorization service.
 */

import { PROOFING_FLOW_APP_ID } from "@/config";

/**
 * All available actions in the proofing flow application.
 * These actions map directly to Cerbos policy definitions in service-lx-permissions.
 *
 * @see https://github.com/infinitaslearning/service-lx-permissions/blob/main/policies/proofing-flow/SCENARIOS.md
 */

/**
 * Global proofing flow actions that apply to the application level.
 * These permissions are checked without project or title context.
 */
export const GLOBAL_ACTIONS = [
  /** Access the proofing flow overview page */
  "access_overview",
  /** Access the create new proofing project page */
  "access_create_page",
  /** Create a new proofing project */
  "create_proofing_project",
  /** Indicates the authenticated user is a supplier/setter */
  "is_supplier",
] as const;

/**
 * Title-specific actions that require project and title context.
 * These permissions are checked with project_id attribute.
 */
export const TITLE_ACTIONS = [
  /** Access the proofing project side sheet */
  "access_side_sheet",
  /** Review PDF documents in a proofing project */
  "review_pdf",
  /** Close the current proofing round */
  "close_proofing_round",
  /** Start a new proofing round */
  "start_proofing_round",
  /** Edit proofing project metadata */
  "edit_metadata",
  /** Manage participants (add/remove) */
  "manage_participants",
  /** Archive a completed proofing title */
  "archive",
  /** Indicates the authenticated user is a supplier/setter */
  "is_supplier",
] as const;

/**
 * Round-specific actions that require project ID and round state context.
 * These permissions are checked with round_status attribute for proper authorization.
 */
export const ROUND_SPECIFIC_ACTIONS = [
  /** Review PDF documents and access annotations page (backend handles role and round state) */
  "review_pdf",
  /** Create annotations on PDF documents (backend handles role and round state) */
  "create_annotations",
  /** Edit annotations created by other users */
  "edit_others_annotation",
  /** Delete annotations created by other users */
  "delete_others_annotation",
  /** Change the status of annotations (e.g., mark as resolved) */
  "change_annotation_status",
  /** Reply to comments on annotations */
  "reply_to_comment",
  "is_supplier",
] as const;

/**
 * Proofing flow appId constant
 * @deprecated Import PROOFING_FLOW_APP_ID from @/config instead
 */
export const appId = PROOFING_FLOW_APP_ID;

/**
 * Request body type for the permissions API endpoint.
 * Matches the contract defined by service-lx-users /api/authz/permissions.
 */
export type PermissionsRequest = {
  /** Application identifier (e.g., "proofing-flow") */
  appId: string;
  /** Optional additional principal attributes */
  principalExtraAttr?: Record<string, unknown>;
  /** Resources to check permissions for */
  resources: Array<{
    resource: {
      /** Resource identifier (e.g., "global" or projectId) */
      id: string;
      /** Resource kind (e.g., "proofing-flow") */
      kind: string;
      /** Optional resource attributes (e.g., { project_id: "123" }) */
      attr?: Record<string, unknown>;
    };
    /** Actions to check for this resource */
    actions: readonly string[];
  }>;
};

/**
 * Response type from the permissions API endpoint.
 * Matches the AuthzDecisionSchema from service-lx-users.
 */
export type PermissionsResponse = {
  decision: {
    cerbosCallId: string;
    requestId: string;
    results: Array<{
      resource: {
        id: string;
        kind: string;
        policyVersion: string;
        scope: string;
      };
      actions: Record<string, string>;
      validationErrors: unknown[];
      outputs: unknown[];
    }>;
  };
};

/**
 * Internal permissions map type.
 * Maps action names to boolean values indicating whether the action is allowed.
 */
export type Permissions = Record<string, boolean>;

/**
 * Builds a permissions request for the authorization service.
 *
 * @param projectId - Optional project ID. If provided, includes project_id in resource attributes.
 * @returns Permission request body ready to send to /api/authz/permissions
 *
 * @example
 * // Global permissions (overview page)
 * const request = buildPermissionsRequest();
 * // { appId: "proofing-flow", resources: [{ resource: { id: "global", kind: "proofing-flow", attr: {} }, actions: [...] }] }
 *
 * @example
 * // Project-specific permissions (side sheet)
 * const request = buildPermissionsRequest("proj-123");
 * // { appId: "proofing-flow", resources: [{ resource: { id: "proj-123", kind: "proofing-flow", attr: { project_id: "proj-123" } }, actions: [...] }] }
 */
export function buildPermissionsRequest(projectId?: string): PermissionsRequest {
  return {
    appId,
    resources: [
      {
        resource: {
          id: projectId ?? "global",
          kind: "proofing-flow",
          attr: projectId ? { project_id: projectId } : {},
        },
        actions: GLOBAL_ACTIONS,
      },
    ],
  };
}

/**
 * Builds a round-specific permissions request for the authorization service.
 * Requires both project ID and round state for proper authorization context.
 *
 * @param projectId - Project ID for the proofing title
 * @param roundState - Current state of the round (e.g., "reviewing", "approving")
 * @returns Permission request body ready to send to /api/authz/permissions
 *
 * @example
 * // Round-specific permissions (document review page)
 * const request = buildRoundPermissionsRequest("proj-123", "approving");
 * // { appId: "proofing-flow", resources: [{ resource: { id: "proj-123", kind: "proofing-flow", attr: { project_id: "proj-123", round_status: "approving" } }, actions: ["review_pdf", ...] }] }
 */
export function buildRoundPermissionsRequest(
  projectId: string,
  roundState: string,
): PermissionsRequest {
  return {
    appId,
    resources: [
      {
        resource: {
          id: projectId,
          kind: "proofing-flow",
          attr: {
            project_id: projectId,
            round_status: roundState,
          },
        },
        actions: ROUND_SPECIFIC_ACTIONS,
      },
    ],
  };
}

/**
 * Builds a title-specific permissions request for the authorization service.
 * Uses project ID as the resource identifier for checking title-level permissions.
 *
 * @param projectId - Project ID (from users-and-projects service)
 * @returns Permission request body ready to send to /api/authz/permissions
 *
 * @example
 * // Title-specific permissions (proofing title side sheet)
 * const request = buildTitlePermissionsRequest("proj-123");
 * // { appId: "proofing-flow", resources: [{ resource: { id: "proj-123", kind: "proofing-flow", attr: { project_id: "proj-123" } }, actions: [TITLE_ACTIONS] }] }
 */
export function buildTitlePermissionsRequest(projectId: string): PermissionsRequest {
  return {
    appId,
    resources: [
      {
        resource: {
          id: projectId,
          kind: "proofing-flow",
          attr: {
            project_id: projectId,
          },
        },
        actions: TITLE_ACTIONS,
      },
    ],
  };
}

/**
 * Builds a title-specific permissions request with title state context.
 * Required for state-dependent actions like archive (which requires state='completed').
 *
 * @param projectId - Project ID (from users-and-projects service)
 * @param titleState - Current state of the title (e.g., "draft", "active", "completed", "archived")
 * @returns Permission request body ready to send to /api/authz/permissions
 *
 * @example
 * // Title permissions with state context (for archive button)
 * const request = buildTitlePermissionsWithStateRequest("proj-123", "completed");
 * // { appId: "proofing-flow", resources: [{ resource: { id: "proj-123", kind: "proofing-flow", attr: { project_id: "proj-123", title_state: "completed" } }, actions: [TITLE_ACTIONS] }] }
 */
export function buildTitlePermissionsWithStateRequest(
  projectId: string,
  titleState: string,
): PermissionsRequest {
  return {
    appId,
    resources: [
      {
        resource: {
          id: projectId,
          kind: "proofing-flow",
          attr: {
            project_id: projectId,
            title_state: titleState,
          },
        },
        actions: TITLE_ACTIONS,
      },
    ],
  };
}

/**
 * Transforms the permissions API response into a simple boolean map.
 *
 * Maps Cerbos effects to boolean values:
 * - EFFECT_ALLOW → true
 * - Any other value → false
 *
 * @param response - Raw response from the permissions API
 * @returns Permissions map with action names as keys and boolean values
 *
 * @example
 * const response = {
 *   decision: {
 *     cerbosCallId: "01KFDNJ4WJYFJH38763HVMDYV0",
 *     requestId: "8c64e37a-2c2e-4d6a-8c57-04329bb865a6",
 *     results: [{
 *       resource: { id: "global", kind: "proofing-flow", policyVersion: "", scope: "" },
 *       actions: { "access": "EFFECT_ALLOW", "create_proofing_project": "EFFECT_DENY" },
 *       validationErrors: [],
 *       outputs: []
 *     }]
 *   }
 * };
 * const permissions = transformPermissionsResponse(response);
 * // { access: true, create_proofing_project: false }
 */
export function transformPermissionsResponse(response: PermissionsResponse): Permissions {
  const permissions: Permissions = {};

  // Extract actions from all results in the decision
  for (const result of response.decision.results) {
    for (const [action, effect] of Object.entries(result.actions)) {
      permissions[action] = effect === "EFFECT_ALLOW";
    }
  }

  return permissions;
}
